<template>
  <div class="side-menu">
    <nav>
      <ul>

        <!-- Links shown to all users -->
        <li><router-link to="/">Start</router-link></li>
        <li><router-link to="/about">About</router-link></li>
        <li><router-link to="/quiz/run/seq-demo">▶ Start demo</router-link></li>
        <!--
          The one quiz the team runs. Its predecessors (July, current, May)
          live in the "Old Quizzes" sub-section at the bottom of SEQ Admin, so
          the top of the menu offers a single, unambiguous entry point. The big
          "Start the quiz" button on the home page points at this same route.
        -->
        <li><router-link to="/quiz/run/swiss-eating-quiz-version-2-september-2026">▶ Start quiz (September)</router-link></li>
        <!--
          Report 1 link is hidden from the menu but the /report1 route and
          Report1.vue component remain intact. Re-enable by restoring this:
          <li><router-link to="/report1">Report 1</router-link></li>
        -->


        <!-- Administration + quiz flow + design exploration -->
        <li class="section-divider">───────────────────</li>
        <template v-for="group in menuGroups" :key="group.id">
          <li v-if="groupVisible(group)" class="layout-group">
            <button
              type="button"
              class="section-header collapsible"
              @click="group.open = !group.open"
              :aria-expanded="String(group.open)"
            >
              <span>{{ group.label }}</span>
              <span class="toggle-icon" :class="{ open: group.open }">▾</span>
            </button>
            <ul v-show="group.open" class="sub-menu">
              <template v-for="v in group.variants" :key="v.id">
                <li v-if="variantVisible(v)">
                  <a v-if="v.href" :href="v.href" target="_blank" rel="noopener">{{ v.label }}</a>
                <router-link v-else :to="v.path">{{ v.label }}</router-link>
                </li>
              </template>
              <!-- Optional sub-groups, rendered after the group's own links. -->
              <template v-for="sub in group.groups || []" :key="sub.id">
                <li v-if="groupVisible(sub)" class="layout-group">
                  <button
                    type="button"
                    class="section-header collapsible sub-section-header"
                    @click="sub.open = !sub.open"
                    :aria-expanded="String(sub.open)"
                  >
                    <span>{{ sub.label }}</span>
                    <span class="toggle-icon" :class="{ open: sub.open }">▾</span>
                  </button>
                  <ul v-show="sub.open" class="sub-menu">
                    <template v-for="v in sub.variants" :key="v.id">
                      <li v-if="variantVisible(v)">
                        <a v-if="v.href" :href="v.href" target="_blank" rel="noopener">{{ v.label }}</a>
                        <router-link v-else :to="v.path">{{ v.label }}</router-link>
                      </li>
                    </template>
                  </ul>
                </li>
              </template>
            </ul>
          </li>
        </template>

        <!-- Mock-Ups: design-exploration groups collapsed behind one header -->
        <li v-if="mockupVisible" class="layout-group">
          <button
            type="button"
            class="section-header collapsible"
            @click="mockupSection.open = !mockupSection.open"
            :aria-expanded="String(mockupSection.open)"
          >
            <span>{{ mockupSection.label }}</span>
            <span class="toggle-icon" :class="{ open: mockupSection.open }">▾</span>
          </button>
          <ul v-show="mockupSection.open" class="sub-menu">
            <template v-for="group in mockupSection.groups" :key="group.id">
              <li v-if="groupVisible(group)" class="layout-group">
                <button
                  type="button"
                  class="section-header collapsible sub-section-header"
                  @click="group.open = !group.open"
                  :aria-expanded="String(group.open)"
                >
                  <span>{{ group.label }}</span>
                  <span class="toggle-icon" :class="{ open: group.open }">▾</span>
                </button>
                <ul v-show="group.open" class="sub-menu">
                  <!-- Leaf group: direct variant links. -->
                  <template v-if="group.variants">
                    <template v-for="v in group.variants" :key="v.id">
                      <li v-if="variantVisible(v)">
                        <a v-if="v.href" :href="v.href" target="_blank" rel="noopener">{{ v.label }}</a>
                <router-link v-else :to="v.path">{{ v.label }}</router-link>
                      </li>
                    </template>
                  </template>
                  <!-- Nested group (e.g. Screens): another level of sub-groups. -->
                  <template v-else-if="group.groups">
                    <template v-for="sub in group.groups" :key="sub.id">
                      <li v-if="groupVisible(sub)" class="layout-group">
                        <button
                          type="button"
                          class="section-header collapsible sub-section-header"
                          @click="sub.open = !sub.open"
                          :aria-expanded="String(sub.open)"
                        >
                          <span>{{ sub.label }}</span>
                          <span class="toggle-icon" :class="{ open: sub.open }">▾</span>
                        </button>
                        <ul v-show="sub.open" class="sub-menu">
                          <template v-for="v in sub.variants" :key="v.id">
                            <li v-if="variantVisible(v)">
                              <a v-if="v.href" :href="v.href" target="_blank" rel="noopener">{{ v.label }}</a>
                <router-link v-else :to="v.path">{{ v.label }}</router-link>
                            </li>
                          </template>
                        </ul>
                      </li>
                    </template>
                  </template>
                </ul>
              </li>
            </template>
          </ul>
        </li>

        <!-- Login/Logout moved to bottom -->
        <li class="section-divider">───────────────────</li>
        <li class="login-logout-item">
          <router-link to="/logout-confirmation" @click.native="logout" class="login-logout-link">
            <i class="fas" :class="{ 'fa-sign-in-alt': !loggedIn, 'fa-sign-out-alt': loggedIn }"></i>
            {{ loggedIn ? 'Logout' : 'Login' }}
          </router-link>
        </li>

      </ul>
    </nav>
  </div>
</template>

<script>
export default {
  name: 'SideMenu',
  data() {
    return {
      isAdmin: localStorage.getItem('role') === 'admin',
      isTeacher: localStorage.getItem('role') === 'teacher',
      isOrgAdmin: localStorage.getItem('role') === 'org_admin',
      loggedIn: false,
      menuGroups: [
        {
          id: 'administration',
          label: 'Administration',
          open: true,
          variants: [
            { id: 'profile', label: 'My Account', path: '/profile', roles: ['admin'] },
            { id: 'messages', label: 'My Messages', path: '/messagespage', roles: ['admin'] },
            { id: 'chat', label: 'Chat', path: '/groupchatpage', roles: ['admin'] },
            { id: 'users', label: 'Users', path: '/user-mgmt', roles: ['admin'] },
            { id: 'organizations', label: 'Organizations', path: '/myorganizationspage', roles: ['admin'] },
            { id: 'logins', label: 'Logins', path: '/showloginspage', roles: ['admin'] },
            { id: 'feedback', label: 'Feedback', path: '/feedback-display', roles: ['admin'] },
            { id: 'image-browser', label: 'Image Browser', path: '/images/browse', roles: ['admin'] },
            { id: 'image-uploader', label: 'Image Uploader', path: '/images/upload', roles: ['admin'] },
            { id: 'simulate-quiz', label: 'Simulate Quiz', path: '/simulate-quiz', roles: ['admin'] },
          ],
        },
        {
          id: 'seq-admin',
          label: 'SEQ Admin',
          open: true,
          variants: [
            // Menu hidden from non-admins (roles: ['admin']), same as the
            // Administration group. NOTE: this is UI hiding only; the
            // backend routers are still open via get_current_user_or_dev
            // (anonymous → hardcoded dev user, org_id=14). Restoring real
            // protection means also switching the routers back to
            // Depends(get_current_user). Deferred on purpose.
            // Ordered by containment, outermost first: a survey administers a
            // questionnaire, which is assembled from questions and cards. It
            // is also the order attention moves in once collection starts,
            // since Surveys is a daily page and Questions an authoring one.
            { id: 'surveys', label: 'Surveys', path: '/admin/surveys', roles: ['admin'] },
            { id: 'questionnaires', label: 'Questionnaires', path: '/admin/questionnaires', roles: ['admin'] },
            { id: 'questions', label: 'Questions', path: '/admin/questions', roles: ['admin'] },
            { id: 'cards', label: 'Cards', path: '/admin/cards/edit', roles: ['admin'] },
            { id: 'survey-results', label: 'Survey Results', path: '/admin/survey-results', roles: ['admin'] },
          ],
          // Superseded quizzes, parked at the bottom of SEQ Admin and collapsed
          // by default. They still run, but only the team should reach them.
          groups: [
            {
              id: 'old-quizzes',
              label: 'Old Quizzes',
              open: false,
              variants: [
                { id: 'quiz-july', label: '▶ Start quiz (July)', path: '/quiz/run/swiss-eating-quiz-revision-september-2026', roles: ['admin'] },
                { id: 'quiz-current', label: '▶ Start quiz (current)', path: '/quiz/run/fragebogen-mit-points', roles: ['admin'] },
                { id: 'quiz-may', label: '▶ Start quiz (May)', path: '/quiz/run/swiss-eating-quiz', roles: ['admin'] },
              ],
            },
          ],
        },
      ],
      // Higher-order section that collapses the design-exploration groups
      // behind a single "Mock-Ups" header. Visible to all users; kept
      // collapsed by default so the app opens with a clean menu.
      mockupSection: {
        id: 'mock-ups',
        label: 'Mock-Ups',
        open: false,
        groups: [
          {
            id: 'survey-results',
            label: 'Survey Results',
            open: false,
            variants: [
              { id: 'user-analysis', label: 'User Analysis', path: '/survey-results/user-analysis' },
              { id: 'data-export', label: 'Data Export', path: '/survey-results/data-export' },
            ],
          },
          {
            id: 'user-results',
            label: 'User Results',
            open: false,
            variants: [
              { id: 'r1', label: 'Results 1 · Score-first', path: '/results/1' },
              { id: 'r2', label: 'Results 2 · Radar', path: '/results/2' },
              { id: 'r3', label: 'Results 3 · Conversational', path: '/results/3' },
              { id: 'r4', label: 'Results 4 · Desktop/Email', path: '/results/4' },
              { id: 'r5', label: 'Results 5 · Dietician', path: '/results/5' },
              { id: 'r6', label: 'Results 6 · Health Professional', path: '/results/6' },
              { id: 'r7', label: 'Results 7 · Nutrition - School Teacher', path: '/results/7' },
              { id: 'r8', label: 'Results 8 · Nutrition - Students', path: '/results/8' },
              { id: 'r9', label: 'Results 9 · Sustainability', path: '/results/9' },
              { id: 'r10', label: 'Results 10 · Sustainability - User', path: '/results/10' },
              { id: 'r11', label: 'Results 11 · Sustainability - School Classroom', path: '/results/11' },
              { id: 'r12', label: 'Results 12 · Gen-Z', path: '/results/12' },
            ],
          },
          {
            id: 'advice',
            label: 'Advice',
            open: false,
            variants: [
              { id: 'a1', label: 'Advice 1 · Per group', path: '/advice/1' },
              { id: 'a2', label: 'Advice 2 · Action plan', path: '/advice/2' },
              { id: 'a3', label: 'Advice 3 · Recipes', path: '/advice/3' },
              { id: 'a4', label: 'Advice 4 · Doctor summary', path: '/advice/4' },
              { id: 'a5', label: 'Advice 5 · Basics', path: '/advice/5' },
            ],
          },
          {
            // Everything else (consent, layout mock-ups, post-survey) tucked
            // under one parent so the Mock-Ups menu stays short.
            id: 'screens',
            label: 'Screens',
            open: false,
            groups: [
              {
                id: 'card-layouts',
                label: 'Card layouts',
                open: false,
                variants: [
                  // Bundled standalone HTML mockup, rendered in an iframe by
                  // WideLayoutsMockup.vue (a real route, so it survives the
                  // SPA history fallback).
                  { id: 'wide', label: 'Wide screen', path: '/mockups/card-wide-layouts' },
                ],
              },
              {
                id: 'consent',
                label: 'Consent',
                open: false,
                variants: [
                  { id: 'age', label: 'Path chooser', path: '/consent/age' },
                  { id: 'adult', label: 'Adult', path: '/consent/adult' },
                  { id: 'child-parental', label: 'Child · parental', path: '/consent/child-parental' },
                  { id: 'child-teacher', label: 'Child · teacher', path: '/consent/child-teacher' },
                ],
              },
              {
                id: 'vanilla',
                label: 'Layout · Vanilla',
                open: false,
                variants: [
                  { id: 'a', label: 'A · Card stack', path: '/quiz/vanilla/a' },
                  { id: 'b', label: 'B · Sectioned scroll', path: '/quiz/vanilla/b' },
                  { id: 'c', label: 'C · Conversational', path: '/quiz/vanilla/c' },
                  { id: 'd', label: 'D · Hybrid w/ portion primer', path: '/quiz/vanilla/d' },
                  { id: 'e', label: 'E · Categories', path: '/quiz/vanilla/e' },
                  { id: 'f', label: 'F · Dopamine hit', path: '/quiz/vanilla/f' },
                  { id: 'g', label: 'G · Resume quiz', path: '/quiz/vanilla/g' },
                ],
              },
              {
                id: 'editorial',
                label: 'Layout · Editorial',
                open: false,
                variants: [
                  { id: 'a', label: 'A · Card stack', path: '/quiz/editorial/a' },
                  { id: 'b', label: 'B · Sectioned scroll', path: '/quiz/editorial/b' },
                  { id: 'f', label: 'F · Dopamine hit', path: '/quiz/editorial/f' },
                ],
              },
              {
                id: 'postsurvey',
                label: 'Post-survey',
                open: false,
                variants: [
                  { id: 'email', label: 'Email · Reminder', path: '/postsurvey/email' },
                  { id: 'email-results', label: 'Email · With results', path: '/postsurvey/email-results' },
                  { id: 'email-code', label: 'Email · With code', path: '/postsurvey/email-code' },
                  { id: 'thanks', label: 'Thank you', path: '/postsurvey/thanks' },
                ],
              },
            ],
          },
        ],
      },
    };
  },
  created() {
    this.loggedIn = localStorage.getItem('loggedIn') === 'true';
  },
  computed: {
    mockupVisible() {
      return this.mockupSection.groups.some(g => this.groupVisible(g));
    },
  },
  watch: {
    '$route'() {
      this.isAdmin = localStorage.getItem('role') === 'admin';
      this.isTeacher = localStorage.getItem('role') === 'teacher';
      this.isOrgAdmin = localStorage.getItem('role') === 'org_admin';
      this.loggedIn = localStorage.getItem('loggedIn') === 'true';
    }
  },
  methods: {
    logout() {
      localStorage.clear(); // Add this line to clear all localStorage data
      this.$emit('logout');
    },
    variantVisible(v) {
      if (v.requiresLogin && !this.loggedIn) return false;
      if (!v.roles || v.roles.length === 0) return true;
      if (!this.loggedIn) return false;
      return v.roles.some(r => {
        if (r === 'admin') return this.isAdmin;
        if (r === 'org_admin') return this.isOrgAdmin;
        if (r === 'teacher') return this.isTeacher;
        return false;
      });
    },
    groupVisible(group) {
      if (group.variants) return group.variants.some(v => this.variantVisible(v));
      if (group.groups) return group.groups.some(g => this.groupVisible(g));
      return false;
    },
  }
};
</script>

<style scoped>
.side-menu {
  background-color: #ffffff;
  width: 220px;
  height: calc(100vh - 60px);
  padding: 10px;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  font-family: 'Montserrat', 'Avenir', Helvetica, Arial, sans-serif;
  box-shadow: 1px 0 0 rgba(0, 0, 0, 0.1);
}

.side-menu::-webkit-scrollbar {
  display: none;
}

.side-menu:hover {
  scrollbar-width: thin;
}

.side-menu:hover::-webkit-scrollbar {
  display: block;
  width: 6px;
}

.side-menu:hover::-webkit-scrollbar-thumb {
  background-color: #b3b0b0;
  border-radius: 3px;
}

.side-menu:hover::-webkit-scrollbar-track {
  background: transparent;
}

nav ul {
  list-style-type: none;
  padding: 0;
  margin: 0 0 20px 0;
}

nav li {
  margin-bottom: 1px;
}

nav a {
  text-decoration: none;
  color: #555;
  font-weight: 600;
  display: block;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  line-height: 1.2;
  padding: 6px 12px;
  border-radius: 3px;
  transition: all 0.2s ease;
}

nav a:hover {
  text-decoration: none;
  background-color: #42b983;
  color: #ffffff;
}

nav a.router-link-active {
  background-color: #2E7D32;
  color: #ffffff;
  font-weight: 600;
}

.collapsible {
  cursor: pointer;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  border-radius: 6px;
  font-weight: 600;
  transition: all 0.3s ease;
  color: #323232;
  background-color: #f6f6f6;
}

.section-header.collapsible {
  width: 100%;
  border: none;
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
  text-align: left;
}

.section-header:hover {
  background-color: #E8F5E9;
}

.sub-section-header {
  font-size: 13px;
  background-color: #fbfbfb;
  color: #444;
}

.toggle-icon {
  font-size: 12px;
  transition: transform 0.3s ease;
}

.toggle-icon.open {
  transform: rotate(180deg);
}

.sub-menu {
  border-left: 1px solid #e8e9f5;
  padding-left: 2px;
  margin-left: 10px;
  margin-top: 1px;
}

.sub-menu li {
  margin-bottom: 1px;
}

.sub-menu a {
  font-size: 13px;
  font-weight: 500;
  padding: 5px 10px;
}

.layout-group {
  margin-bottom: 4px;
}

.section-divider {
  margin: 3px 0;
  text-align: center;
  font-size: 11px;
  font-weight: normal;
  opacity: 0.4;
  color: #666;
}

.login-logout-item {
  margin-top: 10px;
}

.login-logout-link {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
}

.login-logout-link i {
  width: 16px;
  text-align: center;
}
</style>